import { useState, useEffect, useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate, useGetList, useGetIdentity, useUpdate, useRefresh } from 'react-admin';
import {
  Box,
  Paper,
  List,
  Typography,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TicketListItem } from './TicketListItem';
import { TicketsListSkeleton } from './TicketsListSkeleton';
import { TicketPreviewDialog } from './TicketPreviewDialog';
import type { TicketsListProps } from '../types/tickets.types';
import { TicketTabFilter } from '../types/tickets.types';
import type { Ticket } from '../../../types/chat.types';
import { TicketStatus } from '../../../types/chat.types';
import { wsClient } from '../../../websocket/WebSocketClient';
import { TicketListAction } from '../../../types/websocket-events';
import { useTicketActions } from '../../../hooks/useTicketActions';

// Ticket reducer for managing real-time updates
type TicketAction =
  | { type: TicketListAction.LOAD_TICKETS; payload: Ticket[] }
  | { type: TicketListAction.UPDATE_TICKET; payload: Ticket }
  | { type: TicketListAction.REMOVE_TICKET; payload: number }
  | { type: 'RESET' };

const ticketReducer = (state: Ticket[], action: TicketAction): Ticket[] => {
  switch (action.type) {
    case TicketListAction.LOAD_TICKETS:
      return action.payload;

    case TicketListAction.UPDATE_TICKET: {
      const ticket = action.payload;
      const index = state.findIndex((t) => t.id === ticket.id);
      if (index !== -1) {
        const newState = [...state];
        newState[index] = ticket;
        return newState;
      }
      // Add new ticket at the beginning
      return [ticket, ...state];
    }

    case TicketListAction.REMOVE_TICKET: {
      return state.filter((t) => t.id !== action.payload);
    }

    case 'RESET':
      return [];

    default:
      return state;
  }
};

/**
 * TicketsList - Container for displaying filtered tickets with infinite scroll
 *
 * Uses assignment-based tab filtering with optional status/priority/channel filters.
 */
export function TicketsList({
  tab,
  searchParam,
  statusFilter,
  priorityFilter,
  selectedQueueIds = [],
  channelFilter,
  sortField = 'updatedAt',
  sortOrder = 'DESC',
  onTicketSelect,
  selectedTicketId,
  onContextMenu,
}: TicketsListProps) {
  const navigate = useNavigate();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [update] = useUpdate();
  const refresh = useRefresh();

  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(ticketReducer, []);
  const [hasMore, setHasMore] = useState(true);
  const [previewTicket, setPreviewTicket] = useState<Ticket | null>(null);

  // Spy action - view ticket without marking as read
  const { spy: spyOnTicket } = useTicketActions();

  // Ensure selectedQueueIds is always an array
  const queueIds = Array.isArray(selectedQueueIds) ? selectedQueueIds : [];

  // Build filter based on tab and additional filter options
  const buildFilter = () => {
    const filter: Record<string, unknown> = {};

    // Tab-based assignment filtering
    switch (tab) {
      case TicketTabFilter.MINE:
        if (identity?.id) {
          filter.assignedUserId_eq = identity.id;
        }
        // Default to open status if no explicit status filter
        if (!statusFilter) {
          filter.status_eq = TicketStatus.OPEN;
        }
        break;
      case TicketTabFilter.UNASSIGNED:
        filter.assignedUserId_null = true;
        // Default to pending status if no explicit status filter
        if (!statusFilter) {
          filter.status_eq = TicketStatus.PENDING;
        }
        break;
      case TicketTabFilter.ALL:
        // No assignment filter
        break;
    }

    // Status dropdown filter (overrides tab defaults when set)
    if (statusFilter) {
      filter.status_eq = statusFilter;
    }

    // Priority filter
    if (priorityFilter) {
      filter.priority_eq = priorityFilter;
    }

    // Search (persistent, works on top of tabs)
    if (searchParam) {
      filter.search_q = searchParam;
    }

    // Queue filter
    if (queueIds.length > 0) {
      filter.ticketQueueId_in = queueIds;
    }

    // Channel filter
    if (channelFilter) {
      filter.channelType_eq = channelFilter;
    }

    return filter;
  };

  // Fetch tickets
  const { data: tickets = [], isLoading, refetch } = useGetList<Ticket>('ticket', {
    pagination: { page: pageNumber, perPage: 50 },
    sort: { field: sortField, order: sortOrder },
    filter: buildFilter(),
  });

  // Fetch pending (unassigned) tickets — only on MINE tab without explicit status filter
  const showPendingSection = tab === TicketTabFilter.MINE && !statusFilter;
  const { data: pendingTickets = [], refetch: refetchPending } = useGetList<Ticket>('ticket', {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'updatedAt', order: 'DESC' },
    filter: {
      assignedUserId_null: true,
      status_eq: TicketStatus.PENDING,
      ...(searchParam ? { search_q: searchParam } : {}),
      ...(queueIds.length > 0 ? { ticketQueueId_in: queueIds } : {}),
    },
  }, { enabled: showPendingSection });

  // Reset when filters change
  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [tab, searchParam, statusFilter, priorityFilter, channelFilter, sortField, sortOrder, JSON.stringify(queueIds)]);

  // Load tickets into state
  useEffect(() => {
    if (tickets.length > 0) {
      if (pageNumber === 1) {
        dispatch({ type: TicketListAction.LOAD_TICKETS, payload: tickets });
      } else {
        // Append for pagination
        tickets.forEach((ticket) => {
          dispatch({ type: TicketListAction.UPDATE_TICKET, payload: ticket });
        });
      }
      setHasMore(tickets.length === 50);
    } else if (pageNumber === 1) {
      dispatch({ type: TicketListAction.LOAD_TICKETS, payload: [] });
      setHasMore(false);
    }
  }, [tickets, pageNumber]);

  // Subscribe to all tickets via WebSocket to receive real-time updates
  useEffect(() => {
    wsClient.subscribeToAllTickets();

    const handleAllSubscribed = () => {
      // Subscribed to real-time updates
    };

    wsClient.on('ticket:all-subscribed', handleAllSubscribed);

    return () => {
      wsClient.off('ticket:all-subscribed', handleAllSubscribed);
    };
  }, []);

  // Subscribe to real-time ticket updates via WebSocket
  useEffect(() => {
    const handleTicketStatusChanged = (payload: any) => {
      const ticketId = payload.ticketId;

      // If ticket was closed and we're viewing it, clear selection
      if (payload.status === TicketStatus.CLOSED && selectedTicketId === ticketId) {
        onTicketSelect?.(null as any);
      }

      refetch();
      refetchPending();
    };

    const handleTicketAssigned = () => {
      refetch();
      refetchPending();
    };

    const handleTicketCreated = () => {
      refresh();
      refetch();
      refetchPending();
    };

    const handleTicketDeleted = (payload: any) => {
      const ticketId = payload.ticketId;

      if (selectedTicketId === ticketId) {
        onTicketSelect?.(null as any);
      }

      dispatch({ type: TicketListAction.REMOVE_TICKET, payload: ticketId });
    };

    const handleContactUpdated = () => {
      refetch();
      refetchPending();
    };

    const handleReconnect = () => {
      wsClient.subscribeToAllTickets();
      refetch();
      refetchPending();
    };

    wsClient.on('ticket:status-changed', handleTicketStatusChanged);
    wsClient.on('ticket:assigned', handleTicketAssigned);
    wsClient.on('ticket:created', handleTicketCreated);
    wsClient.on('ticket:deleted', handleTicketDeleted);
    wsClient.on('contact:updated', handleContactUpdated);
    wsClient.on('connect', handleReconnect);

    return () => {
      wsClient.off('ticket:status-changed', handleTicketStatusChanged);
      wsClient.off('ticket:assigned', handleTicketAssigned);
      wsClient.off('ticket:created', handleTicketCreated);
      wsClient.off('ticket:deleted', handleTicketDeleted);
      wsClient.off('contact:updated', handleContactUpdated);
      wsClient.off('connect', handleReconnect);
    };
  }, [refetch, refetchPending, selectedTicketId, onTicketSelect]);

  // Handle accepting a pending ticket
  const handleAcceptTicket = useCallback(async (ticket: Ticket) => {
    await update(
      'ticket',
      {
        id: ticket.id,
        data: { status: TicketStatus.OPEN },
        previousData: ticket,
      },
      {
        onSuccess: () => {
          if (onTicketSelect) {
            onTicketSelect(ticket);
          } else {
            navigate(`/tickets?ticket=${ticket.id}`);
          }
          refetch();
          refetchPending();
        },
      }
    );
  }, [update, navigate, onTicketSelect, refetch, refetchPending]);

  // Handle spy on ticket (view without marking as read)
  const handleSpyTicket = useCallback(async (ticket: Ticket) => {
    await spyOnTicket(Number(ticket.id));
    setPreviewTicket(ticket);
  }, [spyOnTicket]);

  // Handle ticket click
  const handleTicketClick = useCallback((ticket: Ticket) => {
    if (onTicketSelect) {
      onTicketSelect(ticket);
    } else {
      navigate(`/tickets?ticket=${ticket.id}`);
    }
  }, [navigate, onTicketSelect]);

  // Infinite scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Mini-area for pending tickets — only on MINE tab */}
      {showPendingSection && pendingTickets.length > 0 && (
        <Box
          sx={{
            borderBottom: 2,
            borderColor: 'warning.main',
            maxHeight: 220,
            overflowY: 'auto',
            flexShrink: 0,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 0.75,
              position: 'sticky',
              top: 0,
              bgcolor: 'inherit',
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={600} color="warning.dark">
              {translate('resources.tickets.sections.pending')}
            </Typography>
            <Chip
              label={pendingTickets.length}
              size="small"
              color="warning"
              sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.5 } }}
            />
          </Box>
          <List dense sx={{ pt: 0 }}>
            {pendingTickets.map((ticket) => (
              <TicketListItem
                key={`pending-${ticket.id}`}
                ticket={ticket as any}
                isSelected={selectedTicketId === ticket.id}
                onAccept={handleAcceptTicket}
                onClick={handleTicketClick}
                onSpy={handleSpyTicket}
                onContextMenu={onContextMenu}
                activeTab={tab}
              />
            ))}
          </List>
        </Box>
      )}

      <Paper
        square
        elevation={0}
        sx={{
          flex: 1,
          overflowY: 'auto',
        }}
        onScroll={handleScroll}
      >
        {/* "In progress" section header when pending section is visible */}
        {showPendingSection && pendingTickets.length > 0 && (
          <Box sx={{ px: 2, py: 0.75 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {translate('resources.tickets.sections.inProgress')}
            </Typography>
          </Box>
        )}

        <List sx={{ pt: 0 }}>
          {/* Empty state */}
          {ticketsList.length === 0 && !isLoading && (
            <Box
              sx={{
                display: 'flex',
                height: 100,
                m: 5,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: 16,
                  fontWeight: 600,
                  m: 0,
                }}
              >
                {translate('resources.tickets.list.noTicketsTitle')}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontSize: 14,
                  lineHeight: 1.4,
                }}
              >
                {translate('resources.tickets.list.noTicketsMessage')}
              </Typography>
            </Box>
          )}

          {/* Ticket list */}
          {ticketsList.map((ticket) => (
            <TicketListItem
              key={ticket.id}
              ticket={ticket as any}
              isSelected={selectedTicketId === ticket.id}
              onAccept={handleAcceptTicket}
              onClick={handleTicketClick}
              onSpy={handleSpyTicket}
              onContextMenu={onContextMenu}
              activeTab={tab}
            />
          ))}

          {/* Loading skeleton */}
          {isLoading && <TicketsListSkeleton />}
        </List>
      </Paper>

      {/* Preview/Spy dialog for tickets */}
      <TicketPreviewDialog
        open={!!previewTicket}
        onClose={() => setPreviewTicket(null)}
        ticket={previewTicket}
        onAccept={previewTicket?.status === TicketStatus.PENDING ? handleAcceptTicket : undefined}
      />
    </Box>
  );
}

export default TicketsList;
